/**
 * Feature Flags Audit Log
 * Tracks all flag enable/disable actions for transparency and rollback tracing
 * 
 * Light QA Contract Compliance:
 * - SAFE RUN: Flag admin displays owner, description, audit log
 * - SAFE ROLLBACK: Audit trail for debugging rollback issues
 */

export interface FlagAuditEntry {
  timestamp: number;
  flag: string;
  action: 'enable' | 'disable' | 'delete';
  owner: string;
  reason: string;
  oldValue: boolean | null;
  newValue: boolean | null;
  userId?: string;
  metadata?: Record<string, any>;
}

const AUDIT_PREFIX = 'flag_audit_';
const MAX_AUDIT_ENTRIES = 500; // Keep last 500 entries to prevent localStorage bloat

class FeatureFlagsAuditLog {
  /**
   * Record a flag change action
   */
  recordChange(
    flag: string,
    action: 'enable' | 'disable' | 'delete',
    oldValue: boolean | null,
    newValue: boolean | null,
    owner: string,
    reason: string,
    metadata?: Record<string, any>
  ): void {
    const entry: FlagAuditEntry = {
      timestamp: Date.now(),
      flag,
      action,
      owner,
      reason,
      oldValue,
      newValue,
      userId: this.getCurrentUserId(),
      metadata
    };

    try {
      const log = this.getLog();
      log.push(entry);

      // Keep only last MAX_AUDIT_ENTRIES to prevent localStorage overflow
      if (log.length > MAX_AUDIT_ENTRIES) {
        log.splice(0, log.length - MAX_AUDIT_ENTRIES);
      }

      localStorage.setItem(`${AUDIT_PREFIX}all`, JSON.stringify(log));

      console.log(
        `[Flag Audit] ${action.toUpperCase()} ${flag} by ${owner}: ${reason}`,
        { oldValue, newValue, metadata }
      );
    } catch (error) {
      console.error('[Flag Audit] Failed to record audit entry', { error, entry });
    }
  }

  /**
   * Get audit log entries
   * @param flag - Optional: filter by specific flag
   * @param limit - Optional: limit number of entries (default: all)
   */
  getLog(flag?: string, limit?: number): FlagAuditEntry[] {
    try {
      const stored = localStorage.getItem(`${AUDIT_PREFIX}all`);
      const log: FlagAuditEntry[] = stored ? JSON.parse(stored) : [];

      let filtered = flag 
        ? log.filter((e) => e.flag === flag)
        : log;

      if (limit) {
        filtered = filtered.slice(-limit);
      }

      return filtered;
    } catch (error) {
      console.error('[Flag Audit] Failed to retrieve audit log', { error });
      return [];
    }
  }

  /**
   * Get recent changes (last 50)
   */
  getRecentChanges(): FlagAuditEntry[] {
    return this.getLog(undefined, 50);
  }

  /**
   * Get changes for a specific flag
   */
  getFlagHistory(flag: string): FlagAuditEntry[] {
    return this.getLog(flag);
  }

  /**
   * Get changes by owner
   */
  getChangesByOwner(owner: string): FlagAuditEntry[] {
    return this.getLog().filter(e => e.owner === owner);
  }

  /**
   * Get changes in date range
   */
  getChangesInRange(startDate: number, endDate: number): FlagAuditEntry[] {
    return this.getLog().filter(
      e => e.timestamp >= startDate && e.timestamp <= endDate
    );
  }

  /**
   * Export audit log as CSV
   */
  exportToCsv(): string {
    const log = this.getLog();
    const headers = ['Timestamp', 'Flag', 'Action', 'Owner', 'Reason', 'Old Value', 'New Value', 'User ID'];
    const rows = log.map(entry => [
      new Date(entry.timestamp).toISOString(),
      entry.flag,
      entry.action,
      entry.owner,
      entry.reason,
      String(entry.oldValue ?? 'null'),
      String(entry.newValue ?? 'null'),
      entry.userId ?? 'unknown'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `\"${cell}\"`).join(','))
    ].join('\n');

    return csvContent;
  }

  /**
   * Clear all audit log entries
   */
  clearLog(): void {
    localStorage.removeItem(`${AUDIT_PREFIX}all`);
    console.warn('[Flag Audit] Audit log cleared');
  }

  /**
   * Get current user ID from localStorage (if available)
   */
  private getCurrentUserId(): string | undefined {
    try {
      // Try to get user ID from auth session
      const authSession = localStorage.getItem('supabase.auth.token');
      if (authSession) {
        const session = JSON.parse(authSession);
        return session?.currentSession?.user?.id;
      }
    } catch {
      // Ignore errors
    }
    return undefined;
  }

  /**
   * Get statistics about audit log
   */
  getStats(): {
    totalEntries: number;
    uniqueFlags: number;
    enableActions: number;
    disableActions: number;
    deleteActions: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  } {
    const log = this.getLog();
    const uniqueFlags = new Set(log.map(e => e.flag)).size;
    const enableActions = log.filter(e => e.action === 'enable').length;
    const disableActions = log.filter(e => e.action === 'disable').length;
    const deleteActions = log.filter(e => e.action === 'delete').length;

    return {
      totalEntries: log.length,
      uniqueFlags,
      enableActions,
      disableActions,
      deleteActions,
      oldestEntry: log.length > 0 ? log[0].timestamp : null,
      newestEntry: log.length > 0 ? log[log.length - 1].timestamp : null
    };
  }
}

// Export singleton instance
export const flagAuditLog = new FeatureFlagsAuditLog();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).flagAuditLog = flagAuditLog;
}
